import { useEffect, useRef, useState } from 'react';

// Automatic logoff after a period of inactivity (HIPAA Security Rule,
// 45 CFR 164.312(a)(2)(iii)). Thirty minutes matches NIST SP 800-63B AAL2
// for an administrative system that holds patient information; a one minute
// warning gives the admin a chance to stay signed in.
export const IDLE_MINUTES = 30;
export const WARN_SECONDS = 60;

// Shared across tabs so activity in one tab keeps the others signed in,
// and a tab that wakes from sleep sees the real last activity time.
export const LAST_ACTIVITY_KEY = 'hca_last_activity';
export const IDLE_LOGOUT_KEY = 'hca_idle_logout';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'wheel', 'touchstart'];
const TICK_MS = 1000;
const WRITE_EVERY_MS = 5000;

// Development only: set localStorage hca_idle_minutes_dev to a small number
// to try the timeout on localhost. Production builds ignore it.
function idleLimitMs() {
  if (process.env.NODE_ENV !== 'production') {
    try {
      const m = Number(localStorage.getItem('hca_idle_minutes_dev'));
      if (m > 0) return m * 60 * 1000;
    } catch { /* storage blocked */ }
  }
  return IDLE_MINUTES * 60 * 1000;
}

function readShared() {
  try { return Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0; } catch { return 0; }
}
function writeShared(ts) {
  try { localStorage.setItem(LAST_ACTIVITY_KEY, String(ts)); } catch { /* storage blocked */ }
}

// Returns { warning, secondsLeft, stay }. Calls onIdle once when the idle
// limit is reached. Activity in this tab or any other tab resets the clock.
export default function useIdleLogout({ onIdle, idleMs = idleLimitMs(), warnMs = WARN_SECONDS * 1000, enabled = true }) {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARN_SECONDS);
  const lastRef = useRef(Date.now());
  const lastWriteRef = useRef(0);
  const firedRef = useRef(false);
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const touch = () => {
    const now = Date.now();
    lastRef.current = now;
    if (now - lastWriteRef.current >= WRITE_EVERY_MS) {
      lastWriteRef.current = now;
      writeShared(now);
    }
  };

  useEffect(() => {
    if (!enabled) return undefined;
    firedRef.current = false;
    lastRef.current = Date.now();
    writeShared(lastRef.current);
    lastWriteRef.current = lastRef.current;

    const onActivity = () => touch();
    const onVisible = () => { if (document.visibilityState === 'visible') touch(); };
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisible);

    const timer = setInterval(() => {
      if (firedRef.current) return;
      const last = Math.max(lastRef.current, readShared());
      const idle = Date.now() - last;
      if (idle >= idleMs) {
        firedRef.current = true;
        setWarning(false);
        onIdleRef.current && onIdleRef.current();
        return;
      }
      if (idle >= idleMs - warnMs) {
        setWarning(true);
        setSecondsLeft(Math.max(1, Math.ceil((idleMs - idle) / 1000)));
      } else {
        setWarning(false);
      }
    }, TICK_MS);

    return () => {
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, onActivity));
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [enabled, idleMs, warnMs]);

  // "Stay signed in": counts as activity and writes straight through so
  // other tabs pick it up at once.
  const stay = () => {
    const now = Date.now();
    lastRef.current = now;
    lastWriteRef.current = now;
    writeShared(now);
    setWarning(false);
  };

  return { warning, secondsLeft, stay };
}
